import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaUserCircle,
  FaEnvelope,
  FaBell,
  FaHome,
  FaSignOutAlt,
} from 'react-icons/fa';
import { logoutUser, fetchAuthenticatedImage, getAuthToken, getProfile } from '../../api/services/authService';
import ProfileAvatar from '../ProfileAvatarContent/ProfileAvatar';
import '../Header/Header.css';
import logo from '../../assets/images/logo.png';

/**
 * Marketing / public chrome: fixed bar with TenantScore logo and auth actions.
 * Matches the header used on /listings so public routes feel consistent.
 */
const PublicSiteHeader = ({ forceGuestView = false }) => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [profileAvatarUrl, setProfileAvatarUrl] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const isAuthenticated = !forceGuestView && !!getAuthToken();

  useEffect(() => {
    if (!isAuthenticated) {
      setProfile(null);
      setProfileAvatarUrl(null);
      return undefined;
    }

    let mounted = true;
    setProfileLoading(true);

    const load = async () => {
      try {
        const profileRes = await getProfile();
        if (!mounted) return;
        const data = profileRes?.data || profileRes;
        setProfile(data || null);
        const photoPath = data?.profilePhotoPath || data?.profilePhotoUrl || '';
        if (photoPath) {
          const blobUrl = await fetchAuthenticatedImage(photoPath);
          if (mounted) setProfileAvatarUrl(blobUrl || null);
        } else if (mounted) {
          setProfileAvatarUrl(null);
        }
      } catch (e) {
        console.error('Failed to load user header state:', e);
      } finally {
        if (mounted) setProfileLoading(false);
      }
    };

    load();

    const handleDocumentClick = (e) => {
      const menu = document.querySelector('.user-avatar-container');
      const mobilePanel = document.querySelector('.mobile-user-menu-panel');
      if (menu && !menu.contains(e.target) && (!mobilePanel || !mobilePanel.contains(e.target))) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleDocumentClick);
    return () => {
      mounted = false;
      document.removeEventListener('mousedown', handleDocumentClick);
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (!userMenuOpen) return undefined;

    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    if (!isMobile) return undefined;

    const scrollY = window.scrollY || window.pageYOffset || 0;
    const originalOverflow = document.body.style.overflow;
    const originalPosition = document.body.style.position;
    const originalTop = document.body.style.top;
    const originalWidth = document.body.style.width;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';

    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.position = originalPosition;
      document.body.style.top = originalTop;
      document.body.style.width = originalWidth;
      window.scrollTo(0, scrollY);
    };
  }, [userMenuOpen]);

  const displayName = useMemo(() => {
    if (!isAuthenticated) return 'Guest';
    const first = profile?.firstName || '';
    const last = profile?.lastName || '';
    const combined = `${first} ${last}`.trim();
    return combined || profile?.email || 'Guest';
  }, [isAuthenticated, profile]);

  return (
    <header className="header">
      <div className="header-left">
        <div
          className="logo-container"
          role="button"
          tabIndex={0}
          onClick={() => navigate('/')}
          onKeyDown={(e) => {
            if (e.key === 'Enter') navigate('/');
          }}
        >
          <img src={logo} alt="TenantScore Logo" className="logo-image" />
        </div>
      </div>
      <div className="header-right">
        {isAuthenticated ? (
          <div className="pl-auth-actions">
            <div className="user-avatar-container">
              <button
                type="button"
                className="user-avatar-btn"
                aria-label="Open My Profile menu"
                onClick={() => setUserMenuOpen((v) => !v)}
              >
                <ProfileAvatar
                  variant="header"
                  imageUrl={profileAvatarUrl}
                  profile={profile}
                  title="My Profile"
                />
                <span className="user-avatar-name">My Profile</span>
              </button>

              {userMenuOpen && (
                <>
                  <div className="mobile-user-menu-overlay" onClick={() => setUserMenuOpen(false)} />
                  <div className="mobile-user-menu-panel" role="menu" aria-label="User menu">
                    <button
                      type="button"
                      className="mobile-user-menu-close"
                      onClick={() => setUserMenuOpen(false)}
                      aria-label="Close user menu"
                    >
                      ×
                    </button>
                    <div className="mobile-user-menu-profile">
                      <ProfileAvatar variant="menu" imageUrl={profileAvatarUrl} profile={profile} imgAlt="" />
                      <div className="mobile-user-menu-text">
                        <div className="mobile-user-menu-name">{displayName}</div>
                        <div className="mobile-user-menu-role">Tenant / Guest</div>
                      </div>
                    </div>

                    <button
                      type="button"
                      className="mobile-user-menu-item"
                      onClick={() => {
                        setUserMenuOpen(false);
                        navigate('/listings');
                      }}
                    >
                      <span className="dropdown-icon" aria-hidden="true">
                        <FaHome />
                      </span>
                      Home
                    </button>
                    <button
                      type="button"
                      className="mobile-user-menu-item"
                      onClick={() => {
                        setUserMenuOpen(false);
                        navigate('/tenant/account');
                      }}
                    >
                      <span className="dropdown-icon" aria-hidden="true">
                        <FaUserCircle />
                      </span>
                      My account
                    </button>
                    <button
                      type="button"
                      className="mobile-user-menu-item"
                      onClick={() => {
                        setUserMenuOpen(false);
                        navigate('/tenant/messages');
                      }}
                    >
                      <span className="dropdown-icon" aria-hidden="true">
                        <FaEnvelope />
                      </span>
                      Messages
                    </button>
                    <button
                      type="button"
                      className="mobile-user-menu-item"
                      onClick={() => {
                        setUserMenuOpen(false);
                        navigate('/tenant/notifications');
                      }}
                    >
                      <span className="dropdown-icon" aria-hidden="true">
                        <FaBell />
                      </span>
                      Notifications
                    </button>
                    <button
                      type="button"
                      className="mobile-user-menu-item mobile-user-menu-item-danger"
                      onClick={() => {
                        logoutUser();
                        setUserMenuOpen(false);
                        navigate('/login');
                      }}
                    >
                      <span className="dropdown-icon" aria-hidden="true">
                        <FaSignOutAlt />
                      </span>
                      Sign out
                    </button>
                  </div>

                  <div className="user-dropdown" role="menu" aria-label="User menu">
                    {profileLoading ? (
                      <div className="profile-loading">Loading...</div>
                    ) : (
                      <div className="profile-info">
                        <div className="profile-header">
                          <ProfileAvatar variant="dropdown" imageUrl={profileAvatarUrl} profile={profile} imgAlt="" />
                          <div className="profile-name">
                            <div className="profile-full-name">{displayName}</div>
                            <div className="profile-role-line">Tenant / Guest</div>
                          </div>
                        </div>
                      </div>
                    )}

                    <button type="button" className="dropdown-item" onClick={() => navigate('/tenant/account')}>
                      <span className="dropdown-icon" aria-hidden="true">
                        <FaUserCircle />
                      </span>
                      My account
                    </button>
                    <button type="button" className="dropdown-item" onClick={() => navigate('/tenant/messages')}>
                      <span className="dropdown-icon" aria-hidden="true">
                        <FaEnvelope />
                      </span>
                      Messages
                    </button>
                    <button type="button" className="dropdown-item" onClick={() => navigate('/tenant/notifications')}>
                      <span className="dropdown-icon" aria-hidden="true">
                        <FaBell />
                      </span>
                      Notifications
                    </button>
                    <button
                      type="button"
                      className="dropdown-item dropdown-item-danger"
                      onClick={() => {
                        logoutUser();
                        navigate('/login');
                      }}
                    >
                      <span className="dropdown-icon" aria-hidden="true">
                        <FaSignOutAlt />
                      </span>
                      Sign out
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="nav-actions">
            <button type="button" className="nav-btn nav-btn-secondary" onClick={() => navigate('/login')}>
              Login
            </button>
            <button type="button" className="nav-btn nav-btn-primary" onClick={() => navigate('/signup')}>
              Create Account
            </button>
          </div>
        )}
      </div>
    </header>
  );
};

export default PublicSiteHeader;
